import { useEffect, useState } from "react";
import Language from "../../lib/Language";
import {
    TOrganizationInView,
    TViewOrgVisibilityValue,
    View,
    ViewName,
    ViewOrgVisibility,
} from "../../models/View";
import Log from "../../lib/Log";
import Icon from "@timeedit/ui-components/components/Icon/Icon";
import type { ItemType } from "@timeedit/ui-components/antd/menu/interface";
import Modal from "@timeedit/ui-components/antd/modal/index";
import NamePromptModal from "./NamePromptModal";
import OrganizationPromptModal from "./OrganizationPromptModal";
import _ from "underscore";
import asyncAPI from "../../lib/TimeEditAsyncAPI";
import Menu from "@timeedit/ui-components/antd/menu/index";
import { Button, Dropdown } from "@timeedit/ui-components/antd";
import {
    DeleteOutlined,
    EditOutlined,
    GlobalOutlined,
    UserOutlined,
} from "@timeedit/ui-components/components/Icon/Icons/index";
import Tooltip from "@timeedit/ui-components/components/Tooltip/Tooltip";
import { TOOLTIP_DELAY } from "../../lib/UIConstants";
import TimeEditAPI from "../../lib/TimeEditAPI";
import { TComponent } from "../../types/utils";

type TViewEditButtonsProps = {
    onRename: (view: View) => void;
    onDelete: (view: View) => void;
    onChangePublic: (view?: View) => void;
    isEditVisible: boolean;
    isPublic: boolean;
    view: ViewName;
};

const ViewEditButtons: TComponent<TViewEditButtonsProps> = ({
    onRename,
    onDelete,
    onChangePublic,
    isEditVisible,
    isPublic,
    view,
}) => {
    const onMakePublicPrivatePressed = (e: React.MouseEvent) => {
        e.stopPropagation();
        View.find(view.id, async (fullView) => {
            if (fullView) {
                const [canMakePublic, canMakePrivate] = (await asyncAPI.okToPublishView(view.id))
                    .parameters;
                if (isPublic && !canMakePrivate) {
                    Log.error(Language.get("user_pane_no_permissions"));
                    return;
                }
                if (!isPublic && !canMakePublic) {
                    Log.error(Language.get("user_pane_no_permissions"));
                    return;
                }
                onChangePublic(fullView as View);
            }
        });
    };

    const onDeletePressed = (e: React.MouseEvent) => {
        e.stopPropagation();
        View.find(view.id, async (fullView) => {
            const [[{ delete: canDelete }]] = (await asyncAPI.okToChangeViews(view.id)).parameters;
            if (!canDelete) {
                Log.error(Language.get("user_pane_no_permissions"));
                return;
            }

            if (fullView) {
                onDelete(fullView as View);
            }
        });
    };

    const onEditPressed = (e: React.MouseEvent) => {
        e.stopPropagation();
        View.find(view.id, async (fullView) => {
            const [[{ modify: canModify }]] = (await asyncAPI.okToChangeViews(view.id)).parameters;
            if (!canModify) {
                Log.error(Language.get("user_pane_no_permissions"));
                return;
            }
            if (fullView) {
                onRename(fullView as View);
            }
        });
    };

    const isNotDefaultView = view.id !== 1;
    return (
        <div
            className="draft-item-actions"
            style={{ flexGrow: "1", alignItems: "right", minWidth: "88px", height: "22px" }}
        >
            {" "}
            {isEditVisible && isNotDefaultView ? (
                <>
                    <Tooltip
                        mouseEnterDelay={TOOLTIP_DELAY}
                        title={Language.get("reservation_list_pref_change_name_title")}
                    >
                        <Button
                            size={"small"}
                            icon={<EditOutlined aria-hidden={true} />}
                            onClick={onEditPressed}
                        />
                    </Tooltip>
                    <Tooltip
                        mouseEnterDelay={TOOLTIP_DELAY}
                        title={
                            isPublic
                                ? Language.get("layer_make_private")
                                : Language.get("layer_make_public")
                        }
                    >
                        <Button
                            style={{ marginLeft: "8px" }}
                            size={"small"}
                            icon={
                                isPublic ? (
                                    <UserOutlined aria-hidden={true} />
                                ) : (
                                    <GlobalOutlined aria-hidden={true} />
                                )
                            }
                            onClick={onMakePublicPrivatePressed}
                        />
                    </Tooltip>
                    <Tooltip mouseEnterDelay={TOOLTIP_DELAY} title={Language.get("dialog_delete")}>
                        <Button
                            style={{ marginLeft: "8px" }}
                            size={"small"}
                            icon={<DeleteOutlined aria-hidden={true} />}
                            onClick={onDeletePressed}
                        />
                    </Tooltip>
                </>
            ) : null}
        </div>
    );
};

type TViewMenuProps = {
    view: View;
    changeView: (id: number) => void;
};

const ViewMenu: TComponent<TViewMenuProps> = (props) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [viewBeingEdited, setViewBeingEdited] = useState<View | null>();
    const [isChangeViewNameModalOpen, setIsChangeViewNameModalOpen] = useState(false);
    const [isNewViewModalOpen, setIsNewViewModalOpen] = useState(false);
    const [isModifiable, setIsModifiable] = useState(false);
    const [views, setViews] = useState<ViewName[]>([]);
    const [organizationPrompt, setOrganizationPrompt] = useState<{
        organizations: TOrganizationInView[];
        resolve: (organization: TOrganizationInView | null) => void;
    } | null>(null);

    const [modal, confirmModalContextHolder] = Modal.useModal();

    const saveView = (view: View, saveNew: boolean, orgState: TViewOrgVisibilityValue) => {
        const newView = view.section?.toJSON();

        // Make new views private by not copying organizations.
        const organizations =
            saveNew || orgState === ViewOrgVisibility.PRIVATE ? [] : view.organizations;

        View.save(view.id, view.name, newView, organizations, orgState, (result) => {
            updateViews();
            if (result[0].details) {
                Log.warning(result[0].details);
                return;
            }
            if (saveNew || orgState === ViewOrgVisibility.PUBLIC) {
                // Why the orgState check?
                props.changeView(result[0].reference);
            }
            Log.info(Language.get("nc_cal_res_side_view_saved", view.name));
        });
    };

    const performDelete = (view: View) => {
        View.delete(view.id, () => {
            Log.info(Language.get("nc_cal_res_side_view_deleted", view.name));
            if (view.id === props.view.id) {
                props.changeView(1);
            }
            updateViews();
        });
    };

    const deleteView = (view: View) => {
        modal.confirm({
            icon: <Icon alt={Language.get("nc_draft_confirm_cancel_title")} id="Warning" />,
            title: Language.get("cal_res_side_view_delete"),
            content: view.name,
            okText: Language.get("nc_list_option_delete_selected"),
            cancelText: Language.get("dialog_cancel"),
            onOk: () => {
                performDelete(view);
            },
        });
    };

    const updateViews = () => {
        View.find(null, (newViews) => {
            if (newViews) {
                const views = _.asArray(newViews) as unknown as ViewName[];
                setViews(views);
            }
        });
    };

    const saveCurrentView = () => {
        saveView(props.view, false, props.view.orgState);
    };

    // Opens the organization dialog and resolves with the picked organization,
    // or with null if the user cancelled.
    const selectOrganizationToAdd = (organizations: TOrganizationInView[]) =>
        new Promise<TOrganizationInView | null>((resolve) => {
            setOrganizationPrompt({ organizations, resolve });
        });

    const saveCurrentViewWithOrg = () => {
        let view = props.view.mutableCopy();
        TimeEditAPI.getOrganizations({}, async (result) => {
            const orgs = result.parameters[0];
            const selectedOrg = await selectOrganizationToAdd(orgs);
            // { id: 19, extid: "" }
            if (!selectedOrg) {
                return;
            }
            view.organizations = [...view.organizations, selectedOrg];
            saveView(view, false, props.view.orgState);
        });
    };

    const saveCurrentViewAs = (newName: string) => {
        const newView = props.view.immutableSet({
            id: 0,
            name: newName,
            orgState: ViewOrgVisibility.PRIVATE,
        });
        saveView(newView, true, newView.orgState);
    };

    const makePublic = (view = props.view) => {
        saveView(view, false, ViewOrgVisibility.PUBLIC);
    };

    const makePrivate = (view = props.view) => {
        saveView(view, false, ViewOrgVisibility.PRIVATE);
    };

    const renameView = (newName: string, view: View) => {
        View.save(
            view.id,
            newName,
            view.section?.toJSON(),
            view.organizations,
            view.orgState,
            (result) => {
                if (result[0].details) {
                    Log.warning(result[0].details);
                    return;
                }
                updateViews();
                props.changeView(view.id);
                Log.info(Language.get("nc_cal_res_side_view_saved", newName));
                setViewBeingEdited(null);
            }
        );
    };

    const initViewData = async () => {
        const [[{ modify: canModify }]] = (await asyncAPI.okToChangeViews(props.view.id))
            .parameters;

        setIsModifiable(canModify);
    };

    useEffect(() => {
        if (props.view) {
            initViewData();
        }
    }, [props.view]);

    useEffect(() => {
        updateViews();
    }, []);

    const sortedViews = [...views].sort((a, b) => {
        // Standard view should be on top
        if (a.id === 1 || b.id === 1) {
            return a.id - b.id;
        }

        // Otherwise sort by name
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
        } else if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
        }
        return 0;
    });

    const [hoveredViewId, setHoveredViewId] = useState(0);

    const privateViews = sortedViews
        .filter((view) => view.orgState === ViewOrgVisibility.PRIVATE)
        .map((view) => {
            return {
                key: view.id,
                extra: (
                    <ViewEditButtons
                        isEditVisible={hoveredViewId === view.id}
                        view={view}
                        isPublic={false}
                        onRename={(view: View) => {
                            setDropdownOpen(false);
                            setViewBeingEdited(view);
                            setIsChangeViewNameModalOpen(true);
                        }}
                        onChangePublic={(view?: View) => makePublic(view)}
                        onDelete={deleteView}
                    />
                ),
                label: view.name,
                onClick: (event) => {
                    props.changeView(parseInt(event.key, 10));
                },
                onMouseEnter: () => {
                    setHoveredViewId(view.id);
                },
                onMouseLeave: () => {
                    setHoveredViewId(0);
                },
            };
        });

    const innerNavigation = [
        {
            children: sortedViews
                .filter((view) => view.orgState !== ViewOrgVisibility.PRIVATE)
                .map((view) => {
                    return {
                        key: view.id,
                        extra: (
                            <ViewEditButtons
                                isEditVisible={hoveredViewId === view.id}
                                view={view}
                                isPublic={true}
                                onRename={(view: View) => {
                                    setDropdownOpen(false);
                                    setViewBeingEdited(view);
                                    setIsChangeViewNameModalOpen(true);
                                }}
                                onChangePublic={(view?: View) => makePrivate(view)}
                                onDelete={deleteView}
                            />
                        ),
                        label: view.name,
                        onClick: (event) => {
                            props.changeView(parseInt(event.key, 10));
                        },
                        onMouseEnter: () => {
                            setHoveredViewId(view.id);
                        },
                        onMouseLeave: () => {
                            setHoveredViewId(0);
                        },
                    };
                }),
            key: "publicViews",
            label: Language.get("cal_res_side_view_public"),
        },
        {
            children:
                privateViews.length > 0
                    ? privateViews
                    : [
                          {
                              key: "noPrivateViews",
                              label: Language.get("nc_view_menu_no_private_views_saved"),
                              disabled: true,
                          },
                      ],
            key: "privateViews",
            label: Language.get("cal_res_side_reservation_private"),
        },
        { type: "divider" },
        {
            key: "saveView",
            label: Language.get("nc_save_current_view"),
            disabled: !isModifiable,
            onClick: (event) => {
                event.domEvent.preventDefault();
                saveCurrentView();
            },
        },
        {
            key: "saveViewAs",
            label: Language.get("nc_save_current_view_as"),
            disabled: false,
            onClick: (event) => {
                event.domEvent.preventDefault();
                setDropdownOpen(false);
                setIsNewViewModalOpen(true);
            },
        },
    ].filter((elm) => elm !== null) as ItemType[];

    if (process.env.NODE_ENV === "development") {
        innerNavigation.push({
            key: "saveViewWithOrg",
            label: "Save with org",
            disabled: false,
            onClick: (event) => {
                event.domEvent.preventDefault();
                setDropdownOpen(false);
                saveCurrentViewWithOrg();
            },
        });
    }

    const changeViewNameModal = (
        <NamePromptModal
            open={isChangeViewNameModalOpen}
            title={Language.get("nc_rename_current_view")}
            onSaveName={(newName) => {
                renameView(newName, viewBeingEdited || props.view);
                setIsChangeViewNameModalOpen(false);
            }}
            oldName={viewBeingEdited?.name ?? props.view?.name}
            onCancel={() => {
                updateViews();
                setIsChangeViewNameModalOpen(false);
            }}
        />
    );

    const newViewModal = (
        <NamePromptModal
            open={isNewViewModalOpen}
            title={Language.get("nc_save_current_view_as")}
            onSaveName={(newName) => {
                saveCurrentViewAs(newName);
                setIsNewViewModalOpen(false);
            }}
            onCancel={() => {
                setIsNewViewModalOpen(false);
            }}
        />
    );

    const selectOrganizationModal = (
        <OrganizationPromptModal
            open={organizationPrompt !== null}
            title={Language.get("cal_res_side_reservation_add_org")}
            organizations={organizationPrompt?.organizations ?? []}
            onSelectOrganization={(organization) => {
                organizationPrompt?.resolve(organization);
                setOrganizationPrompt(null);
            }}
            onCancel={() => {
                organizationPrompt?.resolve(null);
                setOrganizationPrompt(null);
            }}
        />
    );

    return (
        <>
            <Dropdown
                trigger={["click"]}
                open={dropdownOpen}
                onOpenChange={setDropdownOpen}
                destroyOnHidden
                className={`tw:h-full
        tw:flex tw:items-center tw:justify-center tw:overflow-hidden
        tw:text-inherit tw:leading-[0]
      `}
                popupRender={() => (
                    <Menu
                        items={innerNavigation}
                        selectedKeys={props.view ? [`${props.view.id}`] : []}
                        selectable
                        mode="vertical"
                    />
                )}
            >
                <Button
                    style={{ alignSelf: "center" }}
                    className="user-dropdown-trigger-button"
                    type="text"
                    onClick={(e) => {
                        e.preventDefault();
                    }}
                >
                    {Language.get("nc_view_x", props.view?.name)}
                    <Icon id="DownOutlined" size={16} alt={Language.get("menu_help")} />
                </Button>
            </Dropdown>
            {changeViewNameModal}
            {newViewModal}
            {selectOrganizationModal}
            {confirmModalContextHolder}
        </>
    );
};

export default ViewMenu;
